/**
 * Dashboard S475 - 2 pajisje (port 801 ID 1, port 802 ID 2)
 * Dropdown: Kompani + Paisje. Zgjedhje kanalesh (L1, L2, L3, Volt DC, DIN0–4...) → karta me vlera automatike.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { usePushNotifications } from '../hooks/usePushNotifications';

import { BACKEND_URL } from '../config';

const CHANNELS_STORAGE_PREFIX = 's475-dashboard-channels-';
const DEFAULT_CHANNELS = ['DO0', 'DO1', 'DO2', 'DO3'];
/** Kanale të hequra në backend; filtrojmë prefs të vjetra (localStorage / API). */
const REMOVED_S475_CHANNELS = new Set(['AIN0', 'AIN1', 'AIN2', 'AIN3', 'AIN4', 'AIN5', 'DIN5', 'DIN6', 'DIN7']);
const filterDashboardChannelList = (arr) => (Array.isArray(arr) ? arr.filter((k) => !REMOVED_S475_CHANNELS.has(k)) : []);
const CHANNEL_LABELS_BASE = {
  DO0: 'DO0', DO1: 'DO1', DO2: 'DO2', DO3: 'DO3',
  L1: 'L1 Tensioni', L2: 'L2 Tensioni', L3: 'L3 Tensioni',
  L1_W: 'Fuqia L1', L2_W: 'Fuqia L2', L3_W: 'Fuqia L3',
  L1_A: 'Amperat L1 (A)', L2_A: 'Amperat L2 (A)', L3_A: 'Amperat L3 (A)',
  ...Object.fromEntries([...Array(5)].map((_, i) => [`DIN${i}`, `DIN${i}`])),
};
const OUTPUT_KEYS = ['DO0', 'DO1', 'DO2', 'DO3'];

function defaultChannelLabel(ch, t, base) {
  if (ch === 'voltage') return t('voltDc');
  if (ch === 'temp') return t('tempChannel');
  if (ch === 'hum') return t('humidityChannel');
  if (ch === 'kw') return t('totalKwChannel');
  if (ch === 'hz') return t('frequencyChannel');
  return base[ch] || ch;
}

function resolveDashboardChannelLabel(ch, device, t, base) {
  const custom = device?.dashboard_channel_labels?.[ch];
  if (typeof custom === 'string' && custom.trim()) return custom.trim();
  return defaultChannelLabel(ch, t, base);
}

const CONFIG_KEY = 'dashboard-s475-config'; // Ruaj konfigurimin: companyId, deviceId
const STALE_MS = 60 * 1000; // Vlerat fshihen vetëm nëse nuk ka lexim të suksesshëm për më shumë se 1 minutë

function extractChannelValue(values, channelKey) {
  if (!values) return null;
  if (['voltage', 'temp', 'hum', 'kw', 'hz'].includes(channelKey)) return values[channelKey];
  const doV = values.do_values || {};
  if (channelKey.startsWith('DO')) return doV[channelKey];
  const din = values.din || {};
  if (channelKey.startsWith('DIN')) return din[channelKey];
  const lin = values.lin || {};
  if (['L1', 'L2', 'L3'].includes(channelKey)) return lin[channelKey];
  const linW = values.lin_watts || {};
  if (['L1_W', 'L2_W', 'L3_W'].includes(channelKey)) return linW[channelKey.slice(0, 2)];
  const linA = values.lin_amperes || {};
  if (['L1_A', 'L2_A', 'L3_A'].includes(channelKey)) return linA[channelKey.slice(0, 2)];
  return null;
}

/** Formatim vlerash sipas kanalit (Tensioni → VAC pa hapësirë, Total kW → kW njëtoken, Fuqia → W, Amperat → A). */
function formatChannelDisplayString(channelKey, val) {
  if (val == null) return '—';
  if (typeof val !== 'number') return String(val);
  const n = Number.isInteger(val) ? String(val) : val.toFixed(2);
  if (['L1', 'L2', 'L3'].includes(channelKey)) return `${n}VAC`;
  if (channelKey === 'kw') return `${n}kW`;
  if (['L1_W', 'L2_W', 'L3_W'].includes(channelKey)) {
    const w = Number.isInteger(val) ? String(val) : val.toFixed(2);
    return `${w}W`;
  }
  if (['L1_A', 'L2_A', 'L3_A'].includes(channelKey)) {
    const a = Number.isInteger(val) ? String(val) : val.toFixed(2);
    return `${a}A`;
  }
  return n;
}

const DashboardS475 = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const push = usePushNotifications();
  const [companies, setCompanies] = useState([]);
  const [s475Devices, setS475Devices] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [displayChannels, setDisplayChannels] = useState(DEFAULT_CHANNELS);
  const [latestValues, setLatestValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [pushStatus, setPushStatus] = useState(null);
  const [testPushLoading, setTestPushLoading] = useState(false);
  const [testPushMessage, setTestPushMessage] = useState(null);
  const [writingDO, setWritingDO] = useState(false);
  const [lastFetchError, setLastFetchError] = useState(null); // për diagnostikë: pse nuk dalin vlerat
  const lastSuccessAtRef = useRef(null); // kohë e fundit kur morëm vlera me sukses
  const prevDeviceIdRef = useRef(undefined); // për të pastruar vetëm kur ndërrohet pajisja, jo në load

  const getToken = () => localStorage.getItem('access_token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/companies`, { headers: headers() });
      const list = Array.isArray(res.data) ? res.data : [];
      setCompanies(list);
      return list;
    } catch (e) {
      console.error('fetchCompanies', e);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchS475Devices = useCallback(async (companyId) => {
    try {
      const url = companyId ? `${BACKEND_URL}/api/s475/devices?company_id=${encodeURIComponent(companyId)}` : `${BACKEND_URL}/api/s475/devices`;
      const res = await axios.get(url, { headers: headers() });
      const list = Array.isArray(res.data) ? res.data : [];
      setS475Devices(list);
      return list;
    } catch (e) {
      console.error('fetchS475Devices', e);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLatest = useCallback(async () => {
    if (!selectedDevice?.id) return;
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/s475/devices/${selectedDevice.id}/latest`,
        { headers: headers() }
      );
      const data = res.data || {};
      // Normalizo: backend kthen { device_id, ts_unix, ts_iso, values }; ndonjëherë values mund të jetë brenda data.data
      const values = data.values ?? data.data?.values ?? {};
      const payload = {
        device_id: data.device_id ?? selectedDevice.id,
        ts_unix: data.ts_unix ?? data.data?.ts_unix,
        ts_iso: data.ts_iso ?? data.data?.ts_iso,
        values: typeof values === 'object' && values !== null ? values : {},
      };
      setLatestValues(payload);
      lastSuccessAtRef.current = Date.now();
      setLastFetchError(null);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || t('connectionError');
      setLastFetchError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      // Mos i humb vlerat: mbaj të fundit. Fshi vetëm nëse nuk ka lexim të suksesshëm mbi 1 minutë.
      const last = lastSuccessAtRef.current;
      if (last != null && Date.now() - last > STALE_MS) {
        setLatestValues(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice?.id]);

  useEffect(() => {
    if (!push.supported) return;
    axios.get(`${BACKEND_URL}/api/push/status`, { headers: headers() })
      .then((res) => setPushStatus(res.data))
      .catch(() => setPushStatus(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [push.supported, push.registered]);

  const sendTestPush = useCallback(async () => {
    setTestPushMessage(null);
    setTestPushLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/push/test`, {}, { headers: headers() });
      setTestPushMessage(res.data.ok ? res.data.message : res.data.error || t('errorGeneric'));
    } catch (e) {
      setTestPushMessage(e.response?.data?.error || e.message || t('errorGeneric'));
    } finally {
      setTestPushLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const compList = await fetchCompanies();

      let apiSel = { companyId: null, deviceId: null };
      try {
        const r = await axios.get(`${BACKEND_URL}/api/s475/user/dashboard-selection`, { headers: headers() });
        apiSel = {
          companyId: r.data?.companyId != null && r.data.companyId !== '' ? String(r.data.companyId) : null,
          deviceId: r.data?.deviceId != null && r.data.deviceId !== '' ? String(r.data.deviceId) : null,
        };
      } catch (e) {
        /* pa server – përdoret vetëm localStorage */
      }

      let stored = null;
      try {
        const raw = localStorage.getItem(CONFIG_KEY);
        if (raw) stored = JSON.parse(raw);
      } catch (e) {}

      const savedCompanyId = stored?.companyId != null ? String(stored.companyId) : null;
      const savedDeviceId = stored?.deviceId != null ? String(stored.deviceId) : null;

      const pickCompanyId = apiSel.companyId || savedCompanyId;
      const pickDeviceId = apiSel.deviceId || savedDeviceId;

      const c = (pickCompanyId && compList.find((x) => String(x.id) === pickCompanyId)) || compList[0];
      const companyIdForDevices = c?.id ? String(c.id) : null;
      const devList = await fetchS475Devices(companyIdForDevices);
      const d = (pickDeviceId && devList.find((x) => String(x.id) === pickDeviceId)) || devList[0];

      if (c) setSelectedCompany(c);
      if (d) setSelectedDevice(d);
      setLoading(false);
    })();
  }, [fetchCompanies, fetchS475Devices]);

  // Kur ndryshon kompania, ngarko pajisjet e asaj kompanie (mos e fshi pajisjen nëse lista është bosh – që Volt DC të mos zhduket)
  useEffect(() => {
    if (!selectedCompany?.id) return;
    fetchS475Devices(selectedCompany.id).then((devList) => {
      setSelectedDevice((prev) => {
        const stillExists = prev?.id && devList.some((x) => String(x.id) === String(prev.id));
        return stillExists ? prev : (devList[0] ?? prev ?? null);
      });
    });
  }, [selectedCompany?.id, fetchS475Devices]);

  // Pastro vetëm kur ndërron nga një pajisje në tjetër (të dyja të definuara) – jo kur device bëhet null (rifresh listë)
  useEffect(() => {
    const currentId = selectedDevice?.id;
    const prevId = prevDeviceIdRef.current;
    prevDeviceIdRef.current = currentId;
    if (prevId !== undefined && currentId !== undefined && prevId !== currentId) {
      lastSuccessAtRef.current = null;
      setLatestValues(null);
      setLastFetchError(null);
    }
  }, [selectedDevice?.id]);

  useEffect(() => {
    fetchLatest();
    const interval = setInterval(fetchLatest, 5000); // çdo 5 sekonda – vetëm lexim, pa ruajtje në DB
    return () => clearInterval(interval);
  }, [fetchLatest]);

  // Ruaj zgjedhjen e Panelit: localStorage + server (sipas përdoruesit) – me debounce
  useEffect(() => {
    if (loading) return;
    if (selectedCompany?.id == null && selectedDevice?.id == null) return;
    try {
      localStorage.setItem(
        CONFIG_KEY,
        JSON.stringify({
          companyId: selectedCompany?.id ?? null,
          deviceId: selectedDevice?.id ?? null,
        })
      );
    } catch (e) {}
    const t = setTimeout(() => {
      axios
        .post(
          `${BACKEND_URL}/api/s475/save-dashboard-selection`,
          {
            companyId: selectedCompany?.id ?? null,
            deviceId: selectedDevice?.id ?? null,
          },
          { headers: headers() }
        )
        .catch(() => {});
    }, 450);
    return () => clearTimeout(t);
  }, [loading, selectedCompany?.id, selectedDevice?.id]);

  // Ngarko kanalet: së pari nga serveri (për këtë user), pastaj localStorage
  useEffect(() => {
    if (!selectedDevice?.id) {
      setDisplayChannels(DEFAULT_CHANNELS);
      return;
    }
    let cancelled = false;
    (async () => {
      let fromApi = null;
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/s475/devices/${selectedDevice.id}/dashboard-channels`,
          { headers: headers() }
        );
        const ch = res.data?.channels;
        if (Array.isArray(ch) && ch.length) {
          const filtered = filterDashboardChannelList(ch);
          if (filtered.length) fromApi = filtered;
        }
      } catch (e) {
        /* 502 / pa lidhje */
      }
      if (cancelled) return;
      if (fromApi) {
        setDisplayChannels(fromApi);
        return;
      }
      try {
        const raw = localStorage.getItem(`${CHANNELS_STORAGE_PREFIX}${selectedDevice.id}`);
        const saved = raw ? JSON.parse(raw) : null;
        const cleaned = filterDashboardChannelList(saved);
        setDisplayChannels(cleaned.length ? cleaned : DEFAULT_CHANNELS);
      } catch (e) {
        setDisplayChannels(DEFAULT_CHANNELS);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice?.id]);

  const getCurrentDOValues = () => {
    const doV = latestValues?.values?.do_values || {};
    return {
      DO0: !!(doV.DO0 === 1 || doV.DO0 === true),
      DO1: !!(doV.DO1 === 1 || doV.DO1 === true),
      DO2: !!(doV.DO2 === 1 || doV.DO2 === true),
      DO3: !!(doV.DO3 === 1 || doV.DO3 === true),
    };
  };

  const handleToggleDO = async (key) => {
    if (!selectedDevice?.id) return;
    const current = getCurrentDOValues();
    const newVal = !current[key];
    const values = { ...current, [key]: newVal };
    setWritingDO(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/s475/devices/${selectedDevice.id}/write-do`,
        { values, changed: key },
        { headers: headers() }
      );
      toast.success(`${key} ${t('doSetTo')} ${newVal ? 'ON' : 'OFF'}`);
      fetchLatest();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || t('writeError');
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setWritingDO(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500 dark:text-gray-400">Duke ngarkuar...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-full min-h-0 flex flex-col bg-gray-100 dark:bg-[#0f172a] p-4 md:overflow-auto">
        {/* Header – 2 dropdown: Kompani + Paisje */}
        <div className="flex flex-wrap items-center gap-3 mb-4 flex-shrink-0">
          {/* Dropdown Kompani */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowCompanyDropdown(!showCompanyDropdown);
                setShowDeviceDropdown(false);
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155] text-left min-w-[180px] hover:border-cyan-500/50 transition-colors"
            >
              <span className="text-sm font-bold text-gray-900 dark:text-white truncate">
                {selectedCompany?.name || t('selectCompanyPlaceholder')}
              </span>
              <svg className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showCompanyDropdown && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#1e293b] rounded-xl shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-[#334155]">
                {companies.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setSelectedCompany(c);
                      setShowCompanyDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] transition ${String(selectedCompany?.id) === String(c.id) ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 font-medium' : 'text-gray-700 dark:text-white'}`}
                  >
                    {c.name}
                  </button>
                ))}
                {companies.length === 0 && <div className="px-4 py-3 text-gray-500 text-sm">{t('noCompanies')}</div>}
              </div>
            )}
          </div>

          {/* Dropdown Paisje */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowDeviceDropdown(!showDeviceDropdown);
                setShowCompanyDropdown(false);
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155] text-left min-w-[160px] hover:border-cyan-500/50 transition-colors"
            >
              <span className="text-sm font-bold text-gray-900 dark:text-white truncate">
                {selectedDevice?.location_name || selectedDevice?.label || t('selectLocationPlaceholder')}
              </span>
              <svg className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${showDeviceDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showDeviceDropdown && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-[#1e293b] rounded-xl shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-[#334155]">
                {s475Devices.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => {
                      setSelectedDevice(d);
                      setShowDeviceDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] transition ${String(selectedDevice?.id) === String(d.id) ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 font-medium' : 'text-gray-700 dark:text-white'}`}
                  >
                    {d.location_name || d.label}
                  </button>
                ))}
                {s475Devices.length === 0 && <div className="px-4 py-3 text-gray-500 text-sm">{t('noDevices')}</div>}
              </div>
            )}
          </div>
        </div>

        {/* Kanale: në mobile vetëm zona e kartave lëviz me scroll; timestamp mbetet poshtë */}
        <div className="flex-1 min-h-0 flex flex-col items-start w-full overflow-y-auto md:overflow-visible">
          {!selectedDevice?.id ? (
            <div className="text-gray-500 dark:text-gray-400">{t('selectDevicePlaceholder')}</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 w-full pb-2">
              {displayChannels.map((ch) => {
                const label = resolveDashboardChannelLabel(ch, selectedDevice, t, CHANNEL_LABELS_BASE);
                const isOutput = OUTPUT_KEYS.includes(ch);
                if (isOutput) {
                  const doOn = getCurrentDOValues()[ch];
                  return (
                    <button
                      key={ch}
                      type="button"
                      disabled={writingDO}
                      onClick={() => handleToggleDO(ch)}
                      className={`rounded-xl p-4 border min-h-[84px] flex flex-col justify-center text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                        doOn
                          ? 'bg-green-600 dark:bg-green-600 border-green-500 text-white hover:bg-green-500'
                          : 'bg-white dark:bg-[#1e293b] border-gray-200 dark:border-[#334155] hover:bg-gray-50 dark:hover:bg-[#2d3a4f]'
                      }`}
                    >
                      <p className="text-xs opacity-90 mb-0.5">{label}</p>
                      <p className="text-base font-bold tabular-nums">{doOn ? 'ON' : 'OFF'}</p>
                      <p className="text-[10px] opacity-75 mt-0.5">{t('clickToChange')}</p>
                    </button>
                  );
                }
                const val = extractChannelValue(latestValues?.values, ch);
                const str = formatChannelDisplayString(ch, val);
                const isKw = ch === 'kw';
                return (
                  <div
                    key={ch}
                    className="rounded-xl p-4 border min-h-[84px] flex flex-col justify-center text-left bg-white dark:bg-[#1e293b] border-gray-200 dark:border-[#334155]"
                  >
                    <p className="text-xs opacity-90 mb-0.5">{label}</p>
                    <p
                      className={`font-bold tabular-nums whitespace-nowrap ${isKw ? 'text-sm' : 'text-base'}`}
                    >
                      {str}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Fund: në mobile fiks poshtë mbi nav (Layout pb-16); në web mbetet si më parë */}
        <div className="flex-shrink-0 mt-auto pt-3 border-t border-gray-200/80 dark:border-gray-700/80 md:border-t-0 md:pt-4 md:mt-4 bg-gray-100/95 dark:bg-[#0f172a]/95 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none rounded-lg md:rounded-none px-1 md:px-0">
          {latestValues?.ts_iso && (
            <div className="text-xs text-gray-500 dark:text-gray-400 text-right md:text-left">
              {t('lastUpdated')}: {latestValues.ts_iso}
            </div>
          )}
          {lastFetchError && (
            <div className="mt-2 px-3 py-2 rounded-lg bg-amber-500/20 text-amber-800 dark:text-amber-200 text-xs">
              {t('valuesNotReading')}: {lastFetchError}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DashboardS475;
